// pages/admin/TestimonialList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
  MdAdd, MdEdit, MdDelete, MdChat, MdStar,
} from 'react-icons/md';

export default function TestimonialList() {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${base_url}/api/v1/testimonials/admin/list`);
      if (res.data.success) setList(res.data.data || []);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [base_url]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete testimonial from "${name}"?`)) return;
    setActionLoading((p) => ({ ...p, [id]: true }));
    try {
      await axios.delete(`${base_url}/api/v1/testimonials/${id}`);
      setList((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete');
    } finally {
      setActionLoading((p) => ({ ...p, [id]: false }));
    }
  };

  const handleToggle = async (id, current) => {
    setList((prev) =>
      prev.map((t) => (t.id === id ? { ...t, is_active: !current } : t))
    );
    try {
      await axios.patch(`${base_url}/api/v1/testimonials/${id}/toggle`, {
        is_active: !current,
      });
    } catch (err) {
      setList((prev) =>
        prev.map((t) => (t.id === id ? { ...t, is_active: current } : t))
      );
      alert(err?.response?.data?.message || 'Failed to update');
    }
  };

  return (
    <div className="page-wrapper">
      <div className="container-fluid mt-3">
        <div className="card shadow border-0">
          <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="fw-bold mb-0">
              <MdChat className="me-2 text-primary" />
              Testimonials
            </h4>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/dashboard/mbbs/testimonials/create')}
            >
              <MdAdd /> Add Testimonial
            </button>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th>Name</th>
                    <th>Location</th>
                    <th>Testimonial</th>
                    <th style={{ width: 90 }}>Rating</th>
                    <th style={{ width: 80 }}>Sort</th>
                    <th style={{ width: 100 }}>Active</th>
                    <th style={{ width: 160 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(4)].map((_, i) => (
                      <tr key={i}>
                        {[...Array(8)].map((__, j) => (
                          <td key={j}><Skeleton /></td>
                        ))}
                      </tr>
                    ))
                  ) : list.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-5 text-muted">
                        <MdChat size={40} />
                        <div className="mt-2">No testimonials yet</div>
                        <button
                          className="btn btn-sm btn-primary mt-2"
                          onClick={() => navigate('/dashboard/mbbs/testimonials/create')}
                        >
                          Add your first testimonial
                        </button>
                      </td>
                    </tr>
                  ) : (
                    list.map((t, idx) => (
                      <tr key={t.id}>
                        <td>{idx + 1}</td>
                        <td><strong>{t.name}</strong></td>
                        <td>{t.location || '-'}</td>
                        <td style={{ maxWidth: 400 }}>
                          <div
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              fontSize: 14,
                            }}
                          >
                            {t.text}
                          </div>
                        </td>
                        <td>
                          <span className="text-warning">
                            {'★'.repeat(t.rating || 5)}
                            <span className="text-muted">
                              {'★'.repeat(5 - (t.rating || 5))}
                            </span>
                          </span>
                        </td>
                        <td>{t.sort_order ?? 0}</td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={!!t.is_active}
                              onChange={() => handleToggle(t.id, t.is_active)}
                              style={{ cursor: 'pointer' }}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() =>
                                navigate(`/dashboard/testimonials/edit/${t.id}`)
                              }
                            >
                              <MdEdit /> Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              disabled={actionLoading[t.id]}
                              onClick={() => handleDelete(t.id, t.name)}
                            >
                              <MdDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}